import { fetchApi } from '@/requests/api';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY } from './use-get-user-bookmarks';
import type {
  Bookmark,
  Folder,
  WorkEngagementBookmarks,
  WorkEngagementBookmarksArray,
} from '../types';
import {
  addBookmarkToLocation,
  addFolderToLocation,
  type FolderIndexPath,
} from '../utils/bookmark-utils';

export function useSaveWorkEngagementBookmarks(
  workEngagementId: string | null,
  options?: UseMutationOptions<
    WorkEngagementBookmarks,
    Error,
    Omit<WorkEngagementBookmarks, 'work_engagement_id' | 'customer_id'>
  >
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (bookmark) => {
      if (!workEngagementId) {
        throw new Error('Work engagement ID is required');
      }

      const updatedBookmarkWithWorkEngagementId = {
        ...bookmark,
        work_engagement_id: workEngagementId,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarkWithWorkEngagementId),
      });
      if (!res.ok) {
        throw new Error('Failed to create bookmark');
      }
      const data = await res.json();
      return data;
    },
    mutationKey: [
      WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
      workEngagementId ? [workEngagementId] : [],
    ],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });

      const previousBookmarks =
        queryClient.getQueryData<WorkEngagementBookmarksArray>([
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ]);

      queryClient.setQueryData(
        [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
        [variables]
      );
      return { previousBookmarks };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });
    },
    onError: (_err, _variables, context) => {
      // Restore from context
      if (context?.previousBookmarks) {
        queryClient.setQueryData(
          [
            WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
            workEngagementId ? [workEngagementId] : [],
          ],
          context.previousBookmarks
        );
      }
    },
  });
}

export function useSaveWorkEngagementBookmark(workEngagementId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<
    WorkEngagementBookmarksArray,
    Error,
    Omit<Bookmark, 'order'> & {
      folderPath?: FolderIndexPath;
    }
  >({
    mutationFn: async ({ folderPath, ...bookmark }) => {
      if (!workEngagementId) {
        throw new Error('Work engagement ID is required');
      }

      const workEngagementBookmarks =
        queryClient.getQueryData<WorkEngagementBookmarksArray>([
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          [workEngagementId],
        ]);

      const updatedBookmarks = addBookmarkToLocation(
        workEngagementBookmarks?.[0],
        bookmark,
        folderPath,
        'workengagement'
      );

      const updatedBookmarksWithWorkEngagementId = {
        ...updatedBookmarks,
        work_engagement_id: workEngagementId,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarksWithWorkEngagementId),
      });
      if (!res.ok) {
        throw new Error('Failed to create bookmark');
      }
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
        data
      );
      queryClient.invalidateQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });
    },
  });
}

export function useSaveWorkEngagementFolder(workEngagementId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<
    WorkEngagementBookmarksArray,
    Error,
    Folder & {
      folderPath?: FolderIndexPath;
    }
  >({
    mutationFn: async ({ folderPath, ...folder }) => {
      if (!workEngagementId) {
        throw new Error('Work engagement ID is required');
      }

      const workEngagementBookmarks =
        queryClient.getQueryData<WorkEngagementBookmarksArray>([
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          [workEngagementId],
        ]);

      const updatedBookmarks = addFolderToLocation(
        workEngagementBookmarks?.[0],
        folder,
        folderPath,
        'workengagement'
      );

      const updatedBookmarksWithWorkEngagementId = {
        ...updatedBookmarks,
        work_engagement_id: workEngagementId,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarksWithWorkEngagementId),
      });
      if (!res.ok) {
        throw new Error('Failed to create folder');
      }
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
        data
      );
      queryClient.invalidateQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });
    },
  });
}
